import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useResendEmailOtp, useVerifyEmail } from '@/api/auth/queries';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useUser } from '@/providers/user-provider';

export const Route = createFileRoute('/_auth/verify-email/')({
  component: RouteComponent,
});

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
});

function RouteComponent() {
  const { session } = useUser();
  const navigate = useNavigate();
  const { redirect } = useSearch({ strict: false }) as { redirect: string | undefined };
  const verifyEmailMutation = useVerifyEmail();
  const resendEmailOtpMutation = useResendEmailOtp();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: '',
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    const email = session?.user.email;
    if (!email) {
      return;
    }

    await verifyEmailMutation.mutateAsync({ email, otp: data.pin });
    setTimeout(() => {
      if (redirect) {
        return navigate({ to: redirect });
      }

      navigate({ to: '/projects' });
    }, 0);
  }

  async function onResendCode() {
    await resendEmailOtpMutation.mutateAsync();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Verify your email</CardTitle>
        <CardDescription>Enter the verification code sent to your email</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="pb-8">
            <FormField
              control={form.control}
              name="pin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>One-Time Password</FormLabel>
                  <FormControl>
                    <InputOTP maxLength={6} {...field} disabled={form.formState.isSubmitting}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Verifying...' : 'Verify Email'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={resendEmailOtpMutation.isPending}
              onClick={onResendCode}
            >
              {resendEmailOtpMutation.isPending ? 'Sending...' : 'Resend Code'}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
